import React, { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { LineItemProps, ServiceOffering, ServicesProps, OfferingsProps } from '@jbhi-fi/apis/src/services/types';
import {
  InstallInfoBlock,
  InstallInfoBlockProps,
} from '@jbhi-fi/components/src/Attach/ServicesInfoBlock/InstallInfoBlock';
import { IdPrefixProvider, useIdPrefix } from '@jbhi-fi/components/src/IdPrefix';
import { useIsOnScreen } from '@jbhi-fi/components/src/hooks/useIsOnScreen/useIsOnScreen';
import { InstallAccessorySections } from './InstallAccessorySections';
import { InstallTermsNote } from './InstallTermsNote';
import { styles } from './installProductService.css';
import { useCartContext } from '../../../../utils/cart/context/CartContext';
import { getCartLoading, setCartLoading } from '../../../../utils/cart/cartUtils';
import { getServiceOfferings } from '../../../../utils/parseServiceOfferings';
import { transformCartItemForTracking } from '../../../../utils/cart/transformCartItemForTracking';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { trackAddToCart, trackClickCta, trackInstallActions } from '@jbhi-fi/utils/src/ga4Tracking';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import {
  IsAttachedService,
  removePDPServiceSelection,
  removeSelectedServiceSuggestion,
} from '../utils/product-service';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase';
import { getProductServiceVariantId } from '../utils/product-service';
import { addServiceOfferings, buildInstallSuggestionResult, normaliseSku } from './utils';
import type { JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { ServiceIds } from '../../../../utils/constants/services';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { PRODUCT_MESSAGE_TYPES } from '@jbhi-fi/components/src/ProductDetail/ProductMessages/utils/getProductMessages';
import { TvInstallGroup } from '../../../../utils/types';
import { Facets, InstallEventArgs } from './types';

type Props = {
  productSku: string;
  productServiceLink?: string;
  productCartId?: string;
  productLineId?: string;
  variant: 'minicart' | 'cart' | 'slideout';
  gtmSource: 'minicart' | 'cart' | 'slideout';
  eventType: string; // 'TV Install Modal' | 'TV Install Tile'
  suggestions: Array<LineItemProps>;
  preSelectedOfferingId?: string;
  facets?: Facets;
  onAddonSelectionChange?: (installEventArgs: InstallEventArgs) => void;
};

const TrackedInstallInfoBlock = ({ onVisible, ...props }: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useIsOnScreen(containerRef, { persistOnceSeen: true });

  useEffect(() => {
    if (isOnScreen) onVisible();
  }, [isOnScreen, onVisible]);

  return (
    <IdPrefixProvider prefix="install">
      <div ref={containerRef}>
        <InstallInfoBlock {...props} />
      </div>
    </IdPrefixProvider>
  );
};

export const InstallContent = ({
  productSku,
  productServiceLink,
  productCartId,
  productLineId,
  variant,
  gtmSource,
  eventType,
  suggestions,
  preSelectedOfferingId,
  facets,
  onAddonSelectionChange,
}: Props) => {
  const isSlideout = variant === 'slideout';
  const { cart, removeAttachedServices } = useCartContext();
  const { installationMessages } = useShopifyProduct();
  const tvInstallMetafield = window?.Jbhifi?.installConfig?.tvInstallConfig?.tvInstallMetaField;
  const [selectedOfferingId, setSelectedOfferingId] = useState<string | undefined>(preSelectedOfferingId);
  const [isCartReady, setIsCartReady] = useState(true);
  const [shouldInstallAccessoryRenderFlag, setShouldInstallAccessoryRenderFlag] = useState(false);
  const hasLoggedAddonInModalImpression = useRef(false);
  const serviceElementIds = { productServicesWrapper: useIdPrefix('product-services-wrapper') };

  const cartItemInstallationMessages = useMemo(() => {
    if (!cart || isSlideout) return [];
    const lineItem = cart?.items?.find(item => item.line_id === productLineId);
    if (!lineItem) return [];
    const installationMessages =
      lineItem.onlineProduct?.Display?.Messages?.filter(
        message => message.SubType === PRODUCT_MESSAGE_TYPES.INSTALLATION
      ) ?? [];
    return installationMessages.length ? toCamelCase(installationMessages) : [];
  }, [productLineId]);

  const { currentSuggestion, removalSuggestion: oldApplicationRemovalSuggestion } = useMemo(
    () =>
      buildInstallSuggestionResult({
        suggestions,
        productSku,
        tvInstallGroups: tvInstallMetafield?.tvInstallGroups,
      }),
    [suggestions, productSku, tvInstallMetafield?.tvInstallGroups]
  );

  const service = useMemo<ServicesProps | undefined>(() => {
    return currentSuggestion?.services?.find(s => s?.shouldRender) ?? currentSuggestion?.services?.[0];
  }, [currentSuggestion]);

  useEffect(() => {
    if (productCartId) {
      setIsCartReady(false);
      if (cart?.items?.find(item => item.properties?._uuid === productCartId)) {
        setIsCartReady(true);
      }
    }
    if (preSelectedOfferingId && productCartId) {
      const preselected = tryParseJson<ServiceOffering>(
        cart?.items?.find(item => item.properties?._uuid === productCartId && item.properties?._serviceLink)?.properties
          ?._serviceData
      );
      if (preselected?.id?.includes(preSelectedOfferingId)) {
        setIsCartReady(true);
      }
    }
  }, [cart?.items, preSelectedOfferingId, productCartId]);

  const handleAddonImpression = () => {
    if (hasLoggedAddonInModalImpression.current || service?.offerings?.length === 0) return;
    trackInstallActions({
      event: 'addon_impression',
      eventType,
      gtmSource,
      addonOptions: service?.offerings,
    });
    hasLoggedAddonInModalImpression.current = true;
  };

  const handleReturnToCartTracking = () => {
    trackInstallActions({
      event: 'addon_selected',
      eventType,
      gtmSource,
      addonOptions: service?.offerings?.filter(offering => offering.id === selectedOfferingId),
    });
    cart?.items
      .filter(
        item =>
          item?.properties?._serviceType === ServiceIds.Install &&
          item.properties._serviceLink === cart?.items.find(i => i.line_id === productLineId)?.properties?._serviceLink
      )
      .forEach(cartItem => {
        cartItem.properties = {
          ...cartItem.properties,
          _itemListName: 'TV Installs',
          _itemListSlot: gtmSource,
        };
        trackAddToCart({
          location: gtmSource,
          currency: getStoreCurrency(),
          addedItems: transformCartItemForTracking(cartItem),
        });
      });
  };

  const getRemovalIdentifiersForOffering = (offeringId?: string): Array<string> => {
    if (!offeringId) return [];

    const installSkus = new Set<string>([offeringId]);
    const offering =
      service?.offerings?.find(o => o.id === offeringId) ??
      currentSuggestion?.services?.flatMap(s => s.offerings ?? []).find(o => o.id === offeringId);

    const addIdentifier = (value?: string | number | null) => {
      const normalised = normaliseSku(value);
      if (normalised) installSkus.add(normalised);
    };

    if (!offering) return Array.from(installSkus);

    addIdentifier(offering.sku);
    offering.skusIncluded?.forEach(addIdentifier);

    const groups = tvInstallMetafield?.tvInstallGroups ?? {};
    Object.values(groups).forEach((group: TvInstallGroup) => {
      const groupSkus = group?.skus?.filter(Boolean) ?? [];
      const offeringSku = normaliseSku(offering.sku);
      if (groupSkus.includes(offeringId) || (offeringSku && groupSkus.includes(offeringSku))) {
        groupSkus.forEach((identifier: string) => installSkus.add(identifier));
      }
    });

    return Array.from(installSkus);
  };

  const addServiceToExistingItem = async (serviceId: string, offeringIds: Array<string>) => {
    if (!offeringIds.length) return;
    let primaryLineItem: JbCartItem | undefined;
    if (isSlideout) {
      cart?.items?.some(item => {
        if (
          item.sku === productSku &&
          (!item.properties._serviceLink || item.properties._serviceLink === productServiceLink)
        ) {
          primaryLineItem = item;
          return true;
        }
        return false;
      });
    } else {
      cart?.items?.some(item => {
        if (
          item.sku === productSku &&
          item.properties._isAttachedService !== 'true' &&
          item.line_id === productLineId
        ) {
          primaryLineItem = item;
          return true;
        }
        return false;
      });
    }

    if (!primaryLineItem) return;
    setIsCartReady(false);
    setCartLoading(true);

    try {
      const serviceOfferings = getServiceOfferings();
      if (!serviceOfferings) return;

      const serviceOfferingsKey = primaryLineItem.line_id;
      const offeringsForLine = serviceOfferings?.[serviceOfferingsKey] as Array<ServiceOffering>;
      if (!offeringsForLine?.length) return;

      const matchedOfferings = offeringIds
        .map(id =>
          offeringsForLine.find(item => {
            if (!id) return undefined;
            return item.typeId === serviceId && (item.id === id || item.id?.includes(id));
          })
        )
        .filter((offering): offering is ServiceOffering => !!(offering?.shouldRender && offering.sku));

      const uniqueOfferings = Array.from(new Map(matchedOfferings.map(offering => [offering.id, offering])).values());
      if (!uniqueOfferings.length) return;

      const offeringsToAdd: Array<{ variantId: number; serviceData: any }> = [];
      for (const offering of uniqueOfferings) {
        const shopifyVariantId = await getProductServiceVariantId(offering.sku);
        if (!shopifyVariantId) continue;
        const matchedInService = service?.offerings?.find(
          o => o.id === offering.id || normaliseSku(o.sku) === normaliseSku(offering.sku)
        );
        const mergedTitle = matchedInService?.heading ?? matchedInService?.name;
        const serviceData = { ...offering, generalTitle: mergedTitle };
        offeringsToAdd.push({ variantId: shopifyVariantId, serviceData });
      }

      if (!offeringsToAdd.length) return;
      await addServiceOfferings({
        offerings: offeringsToAdd.map(({ variantId, serviceData }) => ({
          variantId,
          location: variant,
          serviceData,
        })),
        primaryLineItem,
      });
    } catch (error) {
      console.error('Failed to add install service', error);
    } finally {
      setIsCartReady(true);
      setCartLoading(false);
      setShouldInstallAccessoryRenderFlag(true);
    }
  };

  const removeServiceFromExistingItem = async (installSkus?: string | Array<string>) => {
    const primaryItem = cart?.items?.find(
      item => item.properties?._uuid === productCartId && item.sku === productSku && item.properties?._serviceLink
    );
    const primaryItemServiceLink = primaryItem?.properties?._serviceLink;

    if (!primaryItem || !primaryItemServiceLink) return;

    const attachedServices = cart?.items?.filter(
      item => IsAttachedService(item) && item.properties?._serviceLink === primaryItemServiceLink
    );
    if (!attachedServices?.length) return;

    const identifiersArray = Array.isArray(installSkus) ? installSkus : installSkus ? [installSkus] : [];
    const normalisedIdentifiers = Array.from(
      new Set(
        identifiersArray
          .map(identifier => normaliseSku(identifier))
          .filter((identifier): identifier is string => !!identifier)
      )
    );

    const servicesToRemove = attachedServices.filter(item => {
      const serviceData = tryParseJson<ServiceOffering>(item.properties?._serviceData);
      if (!serviceData) return false;
      if (!normalisedIdentifiers.length) return true;

      return normalisedIdentifiers.some(identifier => {
        return serviceData.id?.includes(identifier) || serviceData.sku === identifier;
      });
    });

    if (!servicesToRemove.length) return;

    setIsCartReady(false);
    await removeAttachedServices(primaryItem, servicesToRemove);
    setIsCartReady(true);
  };

  const handleServiceSelected = async (currentService?: ServicesProps, offeringId?: string) => {
    if (getCartLoading()) return;
    if (!currentService?.id || !offeringId) return;

    const previousOfferingId = selectedOfferingId;
    if (previousOfferingId && previousOfferingId !== offeringId) {
      const identifiersToRemove = getRemovalIdentifiersForOffering(previousOfferingId);
      await removeServiceFromExistingItem(identifiersToRemove.length ? identifiersToRemove : previousOfferingId);
    }
    setSelectedOfferingId(offeringId);

    const selectedOffering = currentService.offerings?.find(o => o.id === offeringId);
    const includedSkus = selectedOffering?.skusIncluded;

    const offeringIdsToAdd = new Set<string>([offeringId]);
    includedSkus?.forEach(sku => {
      const bundledOffering = currentService.offerings?.find(o => o.sku === sku);
      if (bundledOffering?.id) offeringIdsToAdd.add(bundledOffering.id);
    });

    await addServiceToExistingItem(currentService.id, Array.from(offeringIdsToAdd));

    if (variant === 'slideout' && onAddonSelectionChange) {
      const InstallEventArgs: InstallEventArgs = {
        event: 'addon_selected',
        eventType,
        gtmSource,
        addonOptions: currentService?.offerings?.filter(offering => offering.id === offeringId) ?? [],
      };
      onAddonSelectionChange(InstallEventArgs);
    }
  };

  const handleServiceDeselected = async (offeringId?: string) => {
    if (offeringId) {
      const identifiersToRemove = getRemovalIdentifiersForOffering(offeringId);
      await removeServiceFromExistingItem(identifiersToRemove.length ? identifiersToRemove : offeringId);
    }
    removePDPServiceSelection();
    removeSelectedServiceSuggestion();

    if (!offeringId) {
      setSelectedOfferingId(undefined);
      return;
    }

    if (selectedOfferingId === offeringId) {
      setSelectedOfferingId(undefined);
    }
  };

  const onButtonClick = (event: SyntheticEvent, offering?: OfferingsProps) => {
    event.preventDefault();
    if (getCartLoading()) return;
    if (!offering || !service) return;
    handleServiceSelected(service, offering.id);
  };

  const installInfoBlockProps: InstallInfoBlockProps = {
    heading: 'Get your TV installed',
    headingLogoURL: service?.imageUrl ?? service?.offerings?.[0].imageUrl ?? undefined,
    offerings: service?.offerings ?? [],
    onAdd: (event, offeringId) => {
      const offering = service?.offerings?.find(item => item.id === offeringId);
      if (!offering) return;
      onButtonClick(event, offering);
    },
    onRemove: async offeringId => {
      await handleServiceDeselected(offeringId);
    },
    onTrack: ({ linkText, linkUrl }) => {
      trackClickCta({ linkText, linkUrl, location: gtmSource });
    },
    selectedOfferingId,
    buttonPosition: 'right',
    isCartReady,
    isSlideout,
    installationMessages: isSlideout ? installationMessages : cartItemInstallationMessages,
  };

  return (
    <div id={serviceElementIds.productServicesWrapper} className={styles.slideoutWrapper}>
      <TrackedInstallInfoBlock
        {...installInfoBlockProps}
        headingLogoSize={{ width: 24, height: 24 }}
        onVisible={handleAddonImpression}
      />
      {selectedOfferingId && shouldInstallAccessoryRenderFlag ? (
        <InstallAccessorySections
          variant={variant}
          facets={facets}
          pageType={gtmSource}
          oldApplianceRemovalSuggestion={oldApplicationRemovalSuggestion}
          isCartReady={isCartReady}
          addRemovalService={addServiceToExistingItem}
          removeRemovalService={removeServiceFromExistingItem}
          getCartLoading={getCartLoading}
          selectedOfferingId={selectedOfferingId}
          onReturnToCartTracking={handleReturnToCartTracking}
        />
      ) : null}
      <InstallTermsNote gtmSource={gtmSource} />
    </div>
  );
};
